import { useContext } from "react";
import useApi from "./useApi";
import { MessageContext } from "../contexts/MessageContext";
import useI18n from "./useI18n";

/**
 * Custom hook to manage messages in the message center.
 *
 * @returns {{
 *   fetchJobMessages: (function(string): Promise<*>),
 *   fetchMessages: ((function(): Promise<*>)|*),
 *   createTicket: (function(*): Promise<*|null>),
 *   createComment: (function(string, Object): Promise<*|null>),
 *   getAttachmentData: (function(string, string): Promise<*>),
 *   checkMessageCenterMaintenance: (function(): Promise<boolean>),
 *   getUnreadMessageCount: (function(): number),
 *   sendMessageAction: (function(string, string, string, string): Promise<*>),
 *   setMessageUnread: (function(string): Promise<*>),
 *   sortMessages: (function(*[], string): *[]),
 *   messageCenterMessages: *[]
 * }}
 */
const useMessages = () => {
    const { getJson, putJson, postJson } = useApi()
    const { upsertMessages, setMessages, messages } = useContext(MessageContext);
    const { getLocaleBCP47 } = useI18n();

    /**
     * Fetch message center forms.
     * @returns {Promise<*>}
     */
    const fetchForms = async () => {
        const data = await getJson(`/online/messagecenter/forms?language=${getLocaleBCP47()}`);
        return data.form;
    }

    /**
     * Fetch form fields for a specific form.
     * @param formId
     * @returns {Promise<*>}
     */
    const fetchFormFields = async (formId) => {
        const data = await getJson(`/online/messagecenter/form?formid=${formId}&language=${getLocaleBCP47()}`);
        return data.fields;
    }

    /**
     * Fetch messages for job from the message center. Upserts messages in context.
     * @param {string} jobid - The job ID.
     * @returns {Promise<any>} messages
     */
    const fetchJobMessages = async (jobid) => {
        if (!jobid) {
            return [];
        }
        const data = await getJson(`/online/messagecenter/searchticket?jobid=${jobid}`);
        if (!Array.isArray(data?.tickets)) {
            return [];
        }
        upsertMessages(data.tickets);
        return data.tickets;
    }

    /**
     * Fetch messages from the message center and reset context.
     * @returns {Promise<any[]>} messages
     */
    const fetchMessages = async () => {
        const data = await getJson('/online/messagecenter/searchticket');
        if (!Array.isArray(data?.tickets)) {
            return [];
        }
        setMessages(data.tickets);
        return data.tickets;
    }

    /**
     * Create a new ticket in the message center.
     * @param {*} formData
     * @returns {Promise<*|null>}
     */
    const createTicket = async (formData) => {
        const data = JSON.stringify(formData);
        return await postJson('/online/messagecenter/createticket', data);
    }

    /**
     * Create a new comment in the message center.
     * @param {string} issueIdOrKey - The issue ID or key.
     * @param {Object} formData - The form data containing the comment and attachments.
     * @returns {Promise<any>} comment
     */
    const createComment = async (issueIdOrKey, formData) => {
        const data = JSON.stringify(formData);
        return await postJson(`/online/messagecenter/createcomment?issue_id_or_key=${issueIdOrKey}`, data);
    }

    /**
     * Fetches the attachment data for a given attachment ID and message ID.
     * @param {string} attachmentId - The ID of the attachment.
     * @param {string} messageId - The ID of the message.
     * @returns {Promise<any>} - The attachment data.
     */
    const getAttachmentData = async (attachmentId, messageId) => {
        return await getJson(`/online/messagecenter/attachment?attachment_id=${attachmentId}&issue_id_or_key=${messageId}`)
    }

    /**
     * Post an attachment to the message center.
     * @param {{filename: string, data: string}} fileData
     * @returns {Promise<*|null>}
     */
    const postAttachment = async (fileData) => {
        return await postJson('/online/messagecenter/attachment', fileData);
    }

    /**
     * Check if maintenance is ongoing.
     * @returns {Promise<boolean>}
     */
    const checkMessageCenterMaintenance = async () => {
        const data = await getJson('/online/messagecenter/maintenance');
        return !!data?.maintenance_ongoing;
    }

    /**
     * Count unread messages
     * and return unread message count.
     * @returns {number} number of unread messages
     */
    const getUnreadMessageCount = ()=> {
        let unread = 0;

        if (messages.length > 0) {
            messages.forEach((message) => {
                if (message.current_status.unread > 0) {
                    unread++;
                }
            });
        }

        return unread;
    }

    /**
     * Send a message action to the server.
     * @param {string} messageId
     * @param {string} action
     * @param {string} value
     * @param {string} comment
     * @returns {Promise<*|null>}
     */
    const sendMessageAction = async (messageId, action, value, comment) => {
        const params = {
            ticket_id: messageId,
            action,
            value,
            comment
        }
        return await putJson('/online/messagecenter/ticketaction', JSON.stringify(params));
    }

    /**
     * Update message center message status to read and save the updated messages to sessionStorage.
     * @param {string} messageId
     * @returns {Promise<any>}
     */
    const setMessageUnread = async (messageId) => {
        const data = await putJson(`/online/messagecenter/read?issue_id_or_key=${messageId}&read=1`, {});
        // Find the message with the specified messageId and update its unread status
        const updatedMessages = messages.map(message => {
            if (message.id === messageId) {
                return {
                    ...message,
                    current_status: {
                        ...message.current_status,
                        unread: 0
                    }
                };
            }
            return message;
        });
        setMessages(updatedMessages);
        return data;
    }

    /**
     * Get the order number for the status
     * @param status
     * @returns {number}
     */
    const getStatusOrder = (status) => {
        let orderNumber;

        switch (status) {
            // Action Needed
            case 'Waiting for customer':
                orderNumber = 1;
                break;

            // Resolved
            case 'Canceled':
            case 'Closed':
            case 'Resolved':
                orderNumber = 3;
                break;

            // In Progress
            case 'Waiting for support':
            case 'Waiting for 3rd party':
            case 'In Progress':
            case 'Pending':
            case 'Escalate':
            case 'Escalated':
            default:
                orderNumber = 2;
                break;
        }

        return orderNumber;
    }

    /**
     * Sort messages based on the selected sort option
     *
     * @param {*[]} messages array of messages
     * @param {string} option selected sort option
     */
    const sortMessages = (messages, option) => {
        const sortedMessages = [...messages];
        switch (option) {
            case 'newest':
                sortedMessages.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
                break;
            case 'unread':
                sortedMessages.sort((a, b) => b.current_status.unread - a.current_status.unread);
                break;
            case 'status':
                sortedMessages.sort((a, b) => getStatusOrder(a.current_status.status) - getStatusOrder(b.current_status.status));
                break;
            case 'creditor':
                sortedMessages.sort((a, b) => a.creditor.localeCompare(b.creditor));
                break;
            default:
                break;
        }

        return sortedMessages;
    }

    return {
        fetchForms,
        fetchFormFields,
        fetchJobMessages,
        fetchMessages,
        createTicket,
        createComment,
        getAttachmentData,
        postAttachment,
        checkMessageCenterMaintenance,
        getUnreadMessageCount,
        sendMessageAction,
        setMessageUnread,
        sortMessages,
        messageCenterMessages: messages
    }
}

export default useMessages;
